import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { User, ExternalLink, UserPlus, X, Briefcase, DollarSign } from 'lucide-react'
import { cn } from '@/lib/utils'
import { apiClient } from '@/lib/api'
import { TONE_HEX, toneForStatus } from '@/lib/phaseTone'

interface ScreenPopContact {
  id: string
  firstName: string
  lastName: string
  email?: string
  phone?: string
}

interface RecentJob {
  id: string
  title: string
  status: string
}

interface ScreenPopPayload {
  matched: boolean
  callerNumber?: string
  contact?: ScreenPopContact
  recentJobs?: RecentJob[]
  openBalance?: number
  activeJob?: { id: string; title: string; status: string } | null
  callSessionId?: string
}

interface ScreenPopProps {
  callerNumber: string
  onContactMatched?: (contactId: string) => void
  onViewContact: (id: string) => void
  onCreateContact: (phone: string, callSessionId?: string) => void
  onDismiss: () => void
}

/**
 * Floating notification tile pushed from top during inbound calls.
 * Desktop: floats top-right (8px above IncomingCallToast).
 * Separate dismiss from accept.
 */
export function ScreenPop({
  callerNumber,
  onContactMatched,
  onViewContact,
  onCreateContact,
  onDismiss,
}: ScreenPopProps) {
  const [payload, setPayload] = useState<ScreenPopPayload | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function lookupContact() {
      try {
        // AC-19: single round-trip to the backend screen-pop endpoint
        const { data } = await apiClient.get('/api/telephony/screen-pop', {
          params: { phone: callerNumber },
        })
        const result = data?.data as ScreenPopPayload | undefined
        if (cancelled) return
        if (result) {
          setPayload(result)
          if (result.matched && result.contact) {
            onContactMatched?.(result.contact.id)
          }
        } else {
          setPayload({ matched: false, callerNumber })
        }
      } catch {
        if (!cancelled) setPayload({ matched: false, callerNumber })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    lookupContact()
    return () => { cancelled = true }
  }, [callerNumber])

  const tileStyle: React.CSSProperties = {
    borderRadius: 'var(--r-md)',
    width: 288,
  }
  const tileClass = 'ds-tile animate-scale-in'

  if (loading) {
    return (
      <div className={tileClass} style={tileStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px' }}>
          <div
            className="shimmer"
            style={{ width: 32, height: 32, borderRadius: 'var(--r-pill)' }}
          />
          <span style={{ fontSize: 13, color: 'var(--midnight-text3)' }}>
            Looking up caller…
          </span>
        </div>
      </div>
    )
  }

  const matched = !!payload?.matched
  const contact = payload?.contact
  const recentJobs = payload?.recentJobs ?? []
  const openBalance = payload?.openBalance ?? 0
  const activeJob = payload?.activeJob ?? null

  const displayName = matched && contact
    ? `${contact.firstName} ${contact.lastName}`.trim()
    : 'Unknown caller'

  return (
    <div className={tileClass} style={tileStyle}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          borderBottom: '0.5px solid rgb(var(--fg-rgb) / 0.06)',
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--midnight-text3)',
          }}
        >
          Incoming call
        </span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss screen pop"
          style={{
            width: 22,
            height: 22,
            borderRadius: 'var(--r-pill)',
            border: '0.5px solid rgb(var(--fg-rgb) / 0.08)',
            background: 'rgb(var(--fg-rgb) / 0.04)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--midnight-text3)',
          }}
        >
          <X style={{ width: 11, height: 11 }} />
        </button>
      </div>

      {/* Contact info */}
      <div style={{ padding: '12px 14px' }}>
        <button
          type="button"
          onClick={() => matched && contact && onViewContact(contact.id)}
          disabled={!matched || !contact}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            width: '100%',
            textAlign: 'left',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: matched && contact ? 'pointer' : 'default',
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--r-pill)',
              background: matched ? 'rgb(var(--fg-rgb) / 0.08)' : 'rgb(var(--fg-rgb) / 0.06)',
              border: `0.5px solid ${matched ? 'rgb(var(--fg-rgb) / 0.14)' : 'rgb(var(--fg-rgb) / 0.08)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <User
              style={{
                width: 16,
                height: 16,
                color: matched ? 'rgb(var(--fg-rgb) / 0.70)' : 'rgb(var(--fg-rgb) / 0.40)',
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: 'rgb(var(--fg-rgb))',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayName}
            </p>
            <p
              style={{
                fontSize: 12,
                color: 'var(--midnight-text3)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {contact?.phone || callerNumber}
            </p>
            {matched && contact?.email && (
              <p
                style={{
                  fontSize: 12,
                  color: 'var(--midnight-text3)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {contact.email}
              </p>
            )}
          </div>
        </button>
      </div>

      {/* Active job */}
      {matched && activeJob && (
        <div style={{ padding: '0 14px 10px' }}>
          <p
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--midnight-text3)',
              marginBottom: 6,
            }}
          >
            Active job
          </p>
          {/* AC-38: clicking the originating record navigates to the job detail page */}
          <Link
            to={`/work/${activeJob.id}`}
            onClick={onDismiss}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              textDecoration: 'none',
            }}
          >
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: 'var(--r-sm)',
                background: 'rgb(var(--fg-rgb) / 0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 1,
              }}
            >
              <Briefcase style={{ width: 10, height: 10, color: 'rgb(var(--fg-rgb) / 0.70)' }} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                style={{
                  fontSize: 12,
                  color: 'rgb(var(--fg-rgb) / 0.80)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {activeJob.title}
              </p>
              <p style={{ fontSize: 11, color: 'var(--midnight-text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {activeJob.status}
              </p>
            </div>
          </Link>
        </div>
      )}

      {/* Recent jobs */}
      {matched && recentJobs.length > 0 && (
        <div style={{ padding: '0 14px 10px' }}>
          <p
            style={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--midnight-text3)',
              marginBottom: 6,
            }}
          >
            Recent jobs
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {recentJobs.map((j) => (
              /* AC-38: each row is navigable */
              <Link
                key={j.id}
                to={`/work/${j.id}`}
                onClick={onDismiss}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, textDecoration: 'none' }}
              >
                <p
                  style={{
                    fontSize: 12,
                    color: 'rgb(var(--fg-rgb) / 0.60)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                  }}
                >
                  {j.title}
                </p>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '2px 7px',
                    borderRadius: 'var(--r-pill)',
                    whiteSpace: 'nowrap',
                    ...jobStatusStyle(j.status),
                  }}
                >
                  {j.status}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Open balance */}
      {matched && openBalance > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 14px 10px' }}>
          <DollarSign style={{ width: 12, height: 12, color: 'var(--midnight-butter)' }} />
          <span style={{ fontSize: 12, color: 'var(--midnight-text3)' }}>Open balance:</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--midnight-butter)', fontVariantNumeric: 'tabular-nums' }}>
            ${openBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      )}

      {/* Actions */}
      <div style={{ padding: '0 14px 14px' }}>
        {matched && contact ? (
          <button
            type="button"
            onClick={() => onViewContact(contact.id)}
            style={{
              width: '100%',
              height: 36,
              borderRadius: 'var(--r-pill)',
              border: '0.5px solid rgb(var(--fg-rgb) / 0.08)',
              background: 'rgb(var(--fg-rgb) / 0.04)',
              color: 'rgb(var(--fg-rgb) / 0.60)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              touchAction: 'manipulation',
            }}
          >
            <ExternalLink style={{ width: 12, height: 12 }} />
            View contact
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onCreateContact(callerNumber, payload?.callSessionId)}
            style={{
              width: '100%',
              height: 36,
              borderRadius: 'var(--r-pill)',
              border: '0.5px solid transparent',
              background: 'var(--accent)',
              color: 'var(--accent-ink)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              touchAction: 'manipulation',
            }}
          >
            <UserPlus style={{ width: 12, height: 12 }} />
            Create contact
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function hexAlpha(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

/** Job status pill — one pastel per phase of work (lib/phaseTone): scheduled
 *  and in progress are sky, completed is butter, cancelled is neutral. */
function jobStatusStyle(status: string): React.CSSProperties {
  const tone = toneForStatus(status, 'job')
  if (tone === 'neutral') {
    return { background: 'rgb(var(--fg-rgb) / 0.06)', color: 'var(--midnight-text3)', border: '0.5px solid rgb(var(--fg-rgb) / 0.08)' }
  }
  const hex = TONE_HEX[tone]
  return { background: hexAlpha(hex, 0.14), color: hex, border: `0.5px solid ${hexAlpha(hex, 0.28)}` }
}
